import React from "react";

/** BarChart — SVG bars + HTML labels (avoids the stretched-text bug from preserveAspectRatio="none"). */
export function BarChart({ data, height = 220, color = "#0F5E5A", formatValue = (v) => v }) {
  if (!data?.length) return null;
  const max = Math.max(...data.map((d) => d.value), 1);
  const chartH = height - 44; // reserve room for value labels on top + x-axis labels below

  return (
    <div className="w-full">
      <div className="relative flex items-end gap-2 px-1" style={{ height: chartH + 22 }}>
        {[0.25, 0.5, 0.75].map((r) => (
          <div
            key={r}
            className="absolute left-0 right-0 border-t border-dashed border-slate-200"
            style={{ bottom: `${r * 100}%` }}
          />
        ))}
        {data.map((d, i) => {
          const h = max > 0 ? (d.value / max) * chartH : 0;
          return (
            <div key={i} className="flex-1 flex flex-col items-center justify-end min-w-0 relative z-10" title={`${d.label}: ${formatValue(d.value)}`}>
              <div className="text-[11px] font-bold text-slate-700 mb-1 whitespace-nowrap">{formatValue(d.value)}</div>
              <div
                className="w-full max-w-[48px] rounded-t-md transition-all hover:opacity-85"
                style={{ height: `${Math.max(h, 2)}px`, backgroundColor: color }}
              />
            </div>
          );
        })}
      </div>
      <div className="flex gap-2 px-1 mt-2 border-t border-slate-100 pt-2">
        {data.map((d, i) => (
          <div key={i} className="flex-1 text-center text-[11px] text-slate-500 font-medium whitespace-nowrap overflow-hidden text-ellipsis">
            {d.label}
          </div>
        ))}
      </div>
    </div>
  );
}

/** LineChart — SVG line/area + HTML labels for readability. */
export function LineChart({ data, height = 220, color = "#800020" }) {
  if (!data?.length) return null;
  const max = Math.max(...data.map((d) => d.value), 100);
  const chartH = height - 44;
  const TOP_HEADROOM = 15; // % reserved at top for value labels
  const X_INSET = 4; // % inset on left/right so edge labels aren't clipped
  const xRange = 100 - X_INSET * 2;

  const points = data.map((d, i) => ({
    x: X_INSET + (data.length > 1 ? (i / (data.length - 1)) * xRange : xRange / 2),
    yPct: max > 0 ? (d.value / max) * (100 - TOP_HEADROOM) : 0, // capped to leave headroom
    ...d,
  }));
  const svgPoints = points.map((p) => ({ x: p.x, y: 100 - p.yPct }));
  const path = svgPoints.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
  const areaPath = `${path} L ${svgPoints[svgPoints.length - 1].x} 100 L ${svgPoints[0].x} 100 Z`;

  return (
    <div className="w-full">
      <div className="relative overflow-hidden" style={{ height: chartH + 22 }}>
        {[0.25, 0.5, 0.75].map((r) => (
          <div key={r} className="absolute left-0 right-0 border-t border-dashed border-slate-200" style={{ bottom: `${r * (100 - TOP_HEADROOM)}%` }}/>
        ))}
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
          <path d={areaPath} fill={color} fillOpacity="0.14"/>
          <path d={path} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke"/>
        </svg>
        <div className="absolute inset-0">
          {points.map((p, i) => (
            <div
              key={i}
              className="absolute flex flex-col items-center"
              style={{ left: `${p.x}%`, bottom: `${p.yPct}%`, transform: "translate(-50%, 0)" }}
              title={`${p.label}: ${p.value}%`}
            >
              <div className="text-[11px] font-bold text-slate-700 whitespace-nowrap mb-0.5">{p.value}%</div>
              <div className="w-2.5 h-2.5 rounded-full border-2 bg-white" style={{ borderColor: color }}/>
            </div>
          ))}
        </div>
      </div>
      <div className="flex mt-2 border-t border-slate-100 pt-2">
        {points.map((p, i) => (
          <div key={i} className="flex-1 text-center text-[11px] text-slate-500 font-medium whitespace-nowrap">
            {p.label}
          </div>
        ))}
      </div>
    </div>
  );
}
